import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2,
  Check,
  HandCoins,
  FileSignature,
  BadgeCheck,
  Download,
  Hourglass,
  MailCheck,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-toastify';

import PublicLayout from '../layouts/PublicLayout';
import {
  useFetchOnboarding,
  useStartOnboarding,
  useResendOnboardingLink,
  useSendOnboardingPhoneCode,
  useVerifyOnboardingPhoneCode,
} from '../../services/leads';
// Matches the public /referral brand theme (hardcoded dark-grey, not app tokens):
// #38383B page, #4A4A4E tile, black/dark fields, white headings, neutral-300 body.
import {
  INPUT as DARK_INPUT,
  PAGE_BG,
  useBrandBodyBackground,
} from './brandTheme';

const WHITE_BTN =
  'rounded-md bg-white text-black font-semibold hover:bg-neutral-200 disabled:opacity-50';

// Seconds before "Resend code" can be tapped again (stops SMS hammering).
const RESEND_COOLDOWN_SECONDS = 30;

const Step = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3">
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-white">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-sm font-semibold text-white">{title}</div>
      <div className="text-sm text-neutral-300">{children}</div>
    </div>
  </div>
);

const PartnerOnboarding = () => {
  const { token } = useParams();
  const { data, isLoading, isError, error, refetch } =
    useFetchOnboarding(token);
  const start = useStartOnboarding();
  const resend = useResendOnboardingLink();
  const sendCode = useSendOnboardingPhoneCode();
  const verifyPhone = useVerifyOnboardingPhoneCode();
  const [email, setEmail] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendDone, setResendDone] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  // Applicant finished their packet and is awaiting reviewer approval — set from
  // the start mutation (the intro fetch reports it via data.underReview).
  const [underReview, setUnderReview] = useState(false);

  useBrandBodyBackground();

  // Tick the "Resend code" cooldown down once per second while it's running.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const referrer = data?.referrer;
  const needsEmail = referrer && !referrer.hasEmail;
  // 'direct' = arrived via the /partner-signup link (no veteran referred, no link
  // emailed), so the intro copy drops the referral-specific phrasing.
  const isDirect = data?.source === 'direct';

  const handleStart = async () => {
    if (needsEmail && !email.trim()) {
      toast.error('Please enter your email to continue.');
      return;
    }
    try {
      const res = await start.mutateAsync({
        token,
        email: email.trim() || undefined,
      });
      // Already in the review queue — show the under-review screen, no signing link.
      if (res?.underReview) {
        setUnderReview(true);
        return;
      }
      // Already complete — show the completed screen instead of a signing link.
      if (res?.completed || !res?.token) {
        await refetch();
        return;
      }
      // A full page load, like every other way into signing (QA 2026-10-26):
      // Sentry Replay is left off only when the page opens on /sign/, so an
      // in-app hop recorded the signing token in the replay.
      window.location.assign(`/sign/${res.token}`);
    } catch (e) {
      toast.error(
        e?.response?.data?.msg || 'Could not start. Please try again.',
      );
    }
  };

  const handleSendCode = async () => {
    try {
      await sendCode.mutateAsync(token);
      setCodeSent(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      toast.success('Code sent — check your texts.');
    } catch (e) {
      toast.error(
        e?.response?.data?.msg || 'Could not send a code. Please try again.',
      );
    }
  };

  const handleVerifyCode = async () => {
    try {
      await verifyPhone.mutateAsync({ token, code });
      setPhoneVerified(true);
      toast.success('Phone verified!');
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Invalid code. Please try again.');
    }
  };

  // Expired-link recourse: POST the dead token back; the server re-mints from
  // its (still-authentic) payload and re-emails a fresh onboarding link.
  const handleResendLink = async () => {
    try {
      await resend.mutateAsync({
        token,
        email: resendEmail.trim() || undefined,
      });
      setResendDone(true);
    } catch (e) {
      toast.error(
        e?.response?.data?.msg ||
          'Could not send a new link. Please try again.',
      );
    }
  };

  const shell = (children) => (
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
      headerAlign="left"
    >
      <div className="mx-auto max-w-lg py-10">
        <div className="rounded-2xl bg-[#4A4A4E] p-8 shadow-lg">{children}</div>
      </div>
    </PublicLayout>
  );

  if (isLoading) {
    return shell(
      <div className="flex justify-center py-10 text-neutral-300">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>,
    );
  }

  if (isError) {
    // 410 = invalid-or-expired token. If it's authentic-but-expired, the resend
    // endpoint can re-mint it — so offer a way out instead of a dead end.
    const canRequestNewLink = error?.response?.status === 410;
    return shell(
      <div className="py-6 text-center">
        <h1 className="text-xl font-bold text-white">Link unavailable</h1>
        <p className="mt-2 text-neutral-300">
          {error?.response?.data?.msg || 'This link is invalid or has expired.'}
        </p>
        {canRequestNewLink ? (
          resendDone ? (
            <div className="mt-6 rounded-lg bg-emerald-400/10 p-4">
              <MailCheck className="mx-auto mb-2 h-6 w-6 text-emerald-400" />
              <p className="text-sm text-neutral-300">
                If this link came from a valid signup, a fresh one is on its way
                — check your email.
              </p>
            </div>
          ) : (
            <div className="mt-6 text-left">
              <label
                htmlFor="onboarding-resend-email"
                className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-400"
              >
                Your email
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="onboarding-resend-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={DARK_INPUT}
                />
                <button
                  type="button"
                  onClick={handleResendLink}
                  disabled={resend.isLoading}
                  className={`h-10 shrink-0 whitespace-nowrap px-4 text-sm ${WHITE_BTN}`}
                >
                  {resend.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Request a new link'
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-neutral-400">
                We&apos;ll email you a fresh link to pick up where you left off.
              </p>
            </div>
          )
        ) : null}
      </div>,
    );
  }

  // Application in the review queue (from the intro fetch or the start mutation).
  // Checked before `completed` so an applicant awaiting approval never sees the
  // link+QR success screen — their link + QR arrive by email once approved.
  if (data?.underReview || underReview) {
    return shell(
      <div className="py-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white">
          <Hourglass className="h-7 w-7" />
        </div>
        <h1 className="text-xl font-bold text-white">
          Your application is under review
        </h1>
        <p className="mt-2 text-neutral-300">
          Our team is reviewing your application now. Once you&apos;re approved,
          you&apos;ll get an email with your personal referral link and QR code.
        </p>
      </div>,
    );
  }

  if (data?.completed) {
    const partnerUrl = data.partnerUrl;
    const slug = data.slug;
    const qrSrc = slug
      ? `${process.env.REACT_APP_API_URL}/public/partners/${slug}/qr.png`
      : null;
    const copyLink = async () => {
      try {
        await navigator.clipboard.writeText(partnerUrl);
        toast.success('Link copied!');
      } catch (e) {
        toast.error('Could not copy — please copy it manually.');
      }
    };
    return shell(
      <div className="py-6 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-400/10 text-emerald-400">
          <Check className="h-7 w-7" />
        </div>
        <h1 className="text-xl font-bold text-white">
          {partnerUrl ? 'Thanks for your referral!' : "You're all set!"}
        </h1>
        {partnerUrl ? (
          <>
            <p className="mt-2 text-neutral-300">
              Our team will reach out to them within 24–48 hours.
            </p>
            <h2 className="mt-6 text-xl font-bold text-white">
              Heads up for next time:
            </h2>
            <p className="mt-2 text-neutral-300">
              As a partner, you have the option to share your personal link or
              QR code to your referrals for easy sign up. Just a reminder!
            </p>
            <div className="mt-6 text-left">
              <label
                htmlFor="onboarding-partner-url"
                className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-400"
              >
                Your referral link
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="onboarding-partner-url"
                  readOnly
                  value={partnerUrl}
                  onFocus={(e) => e.target.select()}
                  className={DARK_INPUT}
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className={`h-10 shrink-0 px-4 text-sm ${WHITE_BTN}`}
                >
                  Copy
                </button>
              </div>
            </div>
            {qrSrc ? (
              <div className="mt-6 flex flex-col items-center">
                <img
                  src={qrSrc}
                  alt="Your referral QR code"
                  width={200}
                  height={200}
                  className="rounded-lg border border-white/10 bg-white p-2"
                />
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await fetch(qrSrc);
                      // Error responses are JSON bodies — check before saving
                      // what the partner will PRINT.
                      if (!res.ok)
                        throw new Error(`QR fetch failed (${res.status})`);
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'veritas-referral-qr.png';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      toast.error('Could not download the QR code.');
                    }
                  }}
                  className="mt-3 inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/10"
                >
                  <Download className="h-4 w-4" />
                  Download QR code
                </button>
              </div>
            ) : null}
          </>
        ) : (
          <p className="mt-2 text-neutral-300">
            Your partner paperwork is complete. We&apos;ll reach out about any
            referral rewards you&apos;ve earned.
          </p>
        )}
      </div>,
    );
  }

  return shell(
    <div>
      <div className="mb-5 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white">
          <HandCoins className="h-6 w-6" />
        </div>
        <h1 className="text-xl font-bold text-white">
          {isDirect
            ? 'Become a referral partner'
            : 'Get paid for your referral'}
        </h1>
        <p className="mt-1 text-sm text-neutral-300">
          {isDirect ? (
            <>
              {referrer?.firstName ? `Hi ${referrer.firstName}, h` : 'H'}
              ere&apos;s how the Veritas Ratings referral partner program works.
            </>
          ) : (
            <>
              {referrer?.firstName ? `Hi ${referrer.firstName}, t` : 'T'}hanks
              for referring a veteran to Veritas Ratings. Here&apos;s how the
              reward works.
            </>
          )}
        </p>
      </div>

      <div className="space-y-4">
        <Step icon={HandCoins} title="$250 per successful referral">
          You earn $250 when a veteran you refer becomes a client and their
          claim is successfully completed.
        </Step>
        <Step icon={FileSignature} title="Sign two quick documents">
          To pay you, we need a W-9 and a short partner agreement — both signed
          right here, no account needed.
        </Step>
        <Step icon={BadgeCheck} title="That's it">
          Once your paperwork is on file, you&apos;re set to receive payouts as
          your referrals come through.
        </Step>
      </div>

      {needsEmail ? (
        <div className="mt-6">
          <label
            htmlFor="onboarding-email"
            className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-400"
          >
            Your email
          </label>
          <input
            id="onboarding-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={DARK_INPUT}
          />
        </div>
      ) : null}

      {/* Phone verification — required before any paperwork (enforced server-side too). */}
      <div className="mt-6 rounded-lg bg-black/20 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-white">
          <ShieldCheck className="h-4 w-4" />
          Verify your phone
        </div>
        {phoneVerified ? (
          <p className="mt-1 flex items-center gap-1 text-sm text-emerald-400">
            <Check className="h-4 w-4" /> Phone verified
          </p>
        ) : !codeSent ? (
          <>
            <p className="mt-1 text-xs text-neutral-300">
              We&apos;ll text a 6-digit code to the number
              {referrer?.phoneLast4
                ? ` ending in ${referrer.phoneLast4}`
                : ' on file'}
              .
            </p>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sendCode.isLoading}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 disabled:opacity-50"
            >
              {sendCode.isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Send code
            </button>
          </>
        ) : (
          <>
            <p className="mt-1 text-xs text-neutral-300">
              Enter the 6-digit code we texted you.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                id="onboarding-otp"
                aria-label="6-digit verification code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className={DARK_INPUT}
              />
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={verifyPhone.isLoading || code.length !== 6}
                className={`h-10 shrink-0 px-4 text-sm ${WHITE_BTN}`}
              >
                Verify
              </button>
            </div>
            {/* min-h keeps the tap target ≥40px; the cooldown stops SMS spamming. */}
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sendCode.isLoading || resendCooldown > 0}
              className="mt-1 inline-flex min-h-[40px] items-center px-2 text-xs text-neutral-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:text-neutral-400"
            >
              {resendCooldown > 0
                ? `Resend code (${resendCooldown}s)`
                : 'Resend code'}
            </button>
          </>
        )}
      </div>

      <button
        type="button"
        onClick={handleStart}
        disabled={start.isLoading || !phoneVerified}
        className={`mt-6 inline-flex w-full items-center justify-center gap-2 px-5 py-2.5 text-sm ${WHITE_BTN}`}
      >
        {start.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Get started
      </button>
      {!phoneVerified ? (
        <p className="mt-2 text-center text-xs text-neutral-400">
          Verify your phone above to continue.
        </p>
      ) : null}
      <p className="mt-3 text-center text-xs text-neutral-400">
        {isDirect
          ? 'No pressure — you can come back and finish any time.'
          : 'No pressure — you can complete this any time using your link.'}
      </p>
    </div>,
  );
};

export default PartnerOnboarding;
